/**
 * The app's one door to `localStorage`.
 *
 * Storage can throw outright — Safari in private mode, a blocked origin, a full
 * quota — and everything kept here is a convenience (a remembered choice, a
 * recents list), never something a screen may fail over. So a read that cannot
 * happen is "nothing stored" and a write that cannot happen is skipped; the
 * choice still applies for this session. Callers validate what they read: it is
 * user-editable storage.
 */

/** The stored string, or null when there is none or storage is unavailable. */
export function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error: unknown) {
    if (isStorageUnavailable(error)) return null;
    throw error;
  }
}

export function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (error: unknown) {
    if (!isStorageUnavailable(error)) throw error;
  }
}

/** The values a stored choice may take, and the one used otherwise. */
export interface StoredChoices<T extends string> {
  allowed: readonly T[];
  fallback: T;
}

/**
 * A stored string narrowed to one of `allowed`. Matching against the list drops
 * a stale or tampered value without casting the raw string to `T`.
 */
export function readStoredChoice<T extends string>(
  key: string,
  { allowed, fallback }: StoredChoices<T>,
): T {
  const value = readStored(key);
  return allowed.find((option) => option === value) ?? fallback;
}

/** Browsers report blocked or full storage as a `DOMException` (`SecurityError`, `QuotaExceededError`). */
function isStorageUnavailable(error: unknown): boolean {
  return error instanceof DOMException;
}
